import { ChangeDetectionStrategy, Component, effect, inject } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25LoadingInlineModule } from "../../s25-loading/s25.loading.inline.module";
import { StudentEngagementOrg } from "../StudentEngagementOrg";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { Attribute } from "../../s25-event/EventMicroI";
import { Item } from "../../../pojo/Item";
import { DataIngestionModule } from "../../data-ingestion/data.ingestion.module";
import { AttendanceState } from "../attendance/attendance.service";
import { SEEventDetailsStore } from "./se.event.details.store";
import { AngBridgeUtil } from "../../../util/ang.bridge.util";
import { Proto } from "../../../pojo/Proto";
import { SeOrgSecurityStore } from "../se.org.security.store";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { S25ModalModule } from "../../s25-modal/s25.modal.module";
import { S25CheckboxModule } from "../../s25-checkbox/s25.checkbox.module";
import { EngagementService } from "../engagement-metrics/engagement.service";
import SeOrgTile = StudentEngagementOrg.SeOrgTile;
import { SeLayoutComponent } from "../layouts/se.layout.component";
import { Se25LiveLinkComponent } from "../layouts/se.25live.link.component";

export interface SeEvent extends S25ItemI.NumId {
    date: Proto.ISODateString | string;
    locations?: string[];
    imageAttr?: Attribute;
    imageUrl?: Proto.URLString;
    /** When true, tile search already bulk-resolved event/host-org logos; missing URLs mean no logo. */
    logosResolvedFromSearch?: boolean;
    description?: string;
    hostOrg?: SeOrgTile;
    reservationId?: number;
    additionalOrganizations?: S25ItemI[];
    occurrences?: { reservationId: number; date?: string; locations?: string[]; attendanceState?: AttendanceState }[];
}

@TypeManagerDecorator("s25-ng-se-event-details-view")
@Component({
    selector: "s25-ng-se-event-details-view",
    template: `
        @if (eventStore.loading()) {
            <s25-ng-loading-inline-static></s25-ng-loading-inline-static>
        } @else if (eventStore.portalAccessDenied()) {
            <p class="noResults">This event is not available in Connect25.</p>
        } @else if (eventStore.loaded()) {
            <s25-ng-se-layout [page]="'event-details'" />
            <s25-ng-se-25live-link [itemTypeId]="Item.Ids.Event" [itemId]="eventStore.eventId()" />
        } @else {
            <p class="noResults">Event details could not be found</p>
        }
    `,
    styles: `
        :host {
            display: block;
            margin-bottom: 2rem;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25LoadingInlineModule,
        DataIngestionModule,
        S25DropdownPaginatedModule,
        S25ModalModule,
        S25CheckboxModule,
        SeLayoutComponent,
        Se25LiveLinkComponent,
    ],
})
export class SeEventDetailsViewComponent {
    public eventStore = inject(SEEventDetailsStore);
    public orgSecStore = inject(SeOrgSecurityStore);

    constructor() {
        AngBridgeUtil.init(this);
        effect(() => this.orgSecStore.setOrgId(this.eventStore.hostOrg()?.itemId));
        effect(() => {
            const eventId = this.eventStore.eventId?.();
            if (eventId) EngagementService.recordActivity("EVENT_DETAILS_VIEW", undefined, eventId).catch(() => {});
        });
    }

    protected readonly Item = Item;
}
